import React, { useState } from 'react';
import { X, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { CARD_BACKS } from '../lib/constants';
import { cn } from '../lib/utils';
import { formatCoins } from '../hooks/useCoins';

interface ShopProps {
  user: any;
  onClose: () => void;
  equippedCardBack: string;
  ownedCardBacks: string[];
  coinsBalance: number;
  onEquip: (cardBackId: string) => void;
  onPurchase: (cardBackId: string, price: number) => Promise<boolean>;
}

export default function Shop({
  user,
  onClose,
  equippedCardBack,
  ownedCardBacks,
  coinsBalance,
  onEquip,
  onPurchase,
}: ShopProps) {
  const [purchasingId, setPurchasingId] = useState<string | null>(null);
  const [notification, setNotification] = useState<string | null>(null);

  const showNotification = (msg: string) => {
    setNotification(msg);
    setTimeout(() => setNotification(null), 2000);
  };

  const handlePurchase = async (id: string, price: number) => {
    if (!user) return;
    setPurchasingId(id);
    const success = await onPurchase(id, price);
    setPurchasingId(null);
    if (success) showNotification('Acquisto completato! 🪙');
    else showNotification('Coins insufficienti');
  };

  const items = Object.entries(CARD_BACKS).map(([id, data]) => ({
    id,
    ...data,
    owned: ownedCardBacks.includes(id),
    equipped: equippedCardBack === id,
  }));

  const rarityColors: Record<string, string> = {
    default: 'border-white/20',
    common: 'border-blue-400/40',
    rare: 'border-purple-400/60',
    legendary: 'border-yellow-400/80',
  };

  const rarityBg: Record<string, string> = {
    default: 'bg-[#2a2a4a]',
    common: 'bg-blue-900/60',
    rare: 'bg-purple-900/60',
    legendary: 'bg-gradient-to-br from-yellow-900/60 to-orange-900/60',
  };

  return (
    <motion.div
      className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-gradient-to-b from-[#1a1a2e] to-[#0f0f1e] rounded-2xl w-full max-w-md max-h-[85vh] overflow-hidden flex flex-col"
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div>
            <h2 className="text-lg font-bold text-white">Negozio</h2>
            <div className="flex items-center gap-1 mt-0.5">
              <span className="text-yellow-400 text-sm font-bold">🪙 {formatCoins(coinsBalance)}</span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-white/60" />
          </button>
        </div>

        {/* Shop Items */}
        <div className="overflow-y-auto flex-1 p-4 space-y-3">
          {items.map((item) => (
            <div
              key={item.id}
              className={cn(
                "relative rounded-xl p-3 border transition-all",
                rarityColors[item.rarity] || rarityColors.default,
                item.equipped ? "bg-yellow-400/10" : "bg-white/5"
              )}
            >
              {/* Card back preview */}
              <div className={cn(
                "w-12 h-16 rounded-md mb-2 flex items-center justify-center text-2xl",
                rarityBg[item.rarity] || rarityBg.default
              )}>
                {item.id === 'cb_classic' ? '🃏' : item.id === 'cb_gold' ? '🥇' : '🎴'}
              </div>

              <div className="flex items-start justify-between">
                <div>
                  <div className="font-bold text-white text-sm">{item.name}</div>
                  <div className="text-xs text-white/40 capitalize">{item.rarity}</div>
                  {item.equipped && (
                    <div className="flex items-center gap-1 mt-1 text-green-400 text-xs">
                      <Check className="w-3 h-3" /> Equipaggiato
                    </div>
                  )}
                </div>

                {item.owned ? (
                  item.equipped ? null : (
                    <button
                      onClick={() => onEquip(item.id)}
                      className="px-3 py-1 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-full transition-colors"
                    >
                      Equipaggia
                    </button>
                  )
                ) : (
                  <button
                    onClick={() => handlePurchase(item.id, item.price)}
                    disabled={purchasingId === item.id || coinsBalance < item.price}
                    className={cn(
                      "px-3 py-1 text-xs font-bold rounded-full transition-colors flex items-center gap-1",
                      coinsBalance >= item.price
                        ? "bg-yellow-500 hover:bg-yellow-400 text-black"
                        : "bg-white/10 text-white/30 cursor-not-allowed"
                    )}
                  >
                    🪙 {formatCoins(item.price)}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Notification toast */}
      <AnimatePresence>
        {notification && (
          <motion.div
            className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-white text-black px-4 py-2 rounded-full font-bold text-sm"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
          >
            {notification}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}